import type React from "react"
import { Link } from "react-router-dom"
import Navbar from "../../components/Navbar"
import Footer from "../../components/Footer"
import { CheckCircle, Leaf, Calendar, MapPin, Clock } from "lucide-react"

const GroundCarePage: React.FC = () => {
  const applications = [
    {
      title: "Sports Field Maintenance",
      description: "Professional maintenance for football fields, cricket pitches, and recreational areas",
      image: "https://placehold.co/600x400?text=Sports+Field",
      attachments: ["Cylinder Mower", "Front Mower with Collection", "Rotary Mower", "Watering Arm"],
    },
    {
      title: "Municipal Parks & Gardens",
      description: "Comprehensive care for public parks, gardens, and green spaces",
      image: "https://placehold.co/600x400?text=Parks+Gardens",
      attachments: ["Flail Mower", "Leaf Blower", "Hedge Cutter", "Mulcher"],
    },
    {
      title: "Golf Course Management",
      description: "Precision maintenance for fairways, roughs, and course landscaping",
      image: "https://placehold.co/600x400?text=Golf+Course",
      attachments: ["Cylinder Mower", "Collection System", "Aerator", "Overseeder"],
    },
    {
      title: "Roadside Vegetation",
      description: "Safe and efficient maintenance of roadside verges and embankments",
      image: "https://placehold.co/600x400?text=Roadside+Vegetation",
      attachments: ["Barrier Mower", "Boom Mower", "Flail Mower", "Mulcher"],
    },
  ]

  const attachments = [
    {
      name: "Cylinder Mower",
      description: "Precision cutting for fine turf and sports fields",
      specs: "Multiple cutting widths available",
    },
    {
      name: "Front Mower with Collection System",
      description: "Efficient grass cutting with integrated collection",
      specs: "High-capacity collection hopper",
    },
    {
      name: "Barrier Mower",
      description: "Safe mowing along barriers and guardrails",
      specs: "Hydraulic side-shift capability",
    },
    {
      name: "Flail Mower",
      description: "Heavy-duty cutting for rough terrain",
      specs: "Robust construction for demanding conditions",
    },
    {
      name: "Boom Mower",
      description: "Extended reach for slopes and ditches",
      specs: "Articulated boom with multiple positions",
    },
    {
      name: "Heavy Duty Mulcher",
      description: "Mulching attachment for vegetation management",
      specs: "High-performance mulching system",
    },
    {
      name: "Rotary Mower",
      description: "Versatile cutting for various grass types",
      specs: "Adjustable cutting height",
    },
    {
      name: "Watering Arm",
      description: "Precision watering system",
      specs: "Adjustable spray patterns",
    },
  ]

  const benefits = [
    {
      icon: Calendar,
      title: "Year-Round Operation",
      description: "Maintain grounds in all seasons with versatile attachments",
    },
    {
      icon: Clock,
      title: "Increased Efficiency",
      description: "Complete multiple tasks with one machine, reducing operational time",
    },
    {
      icon: MapPin,
      title: "Precise Control",
      description: "Accurate cutting and maintenance with advanced hydraulic systems",
    },
    {
      icon: Leaf,
      title: "Professional Results",
      description: "Achieve professional-quality results across all ground care applications",
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-900 via-green-800 to-green-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Ground Care Applications
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional
              <br />
              <span className="text-green-400">Ground Care Solutions</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              From sports fields to municipal parks, our ground care solutions deliver precision cutting, efficient
              collection, and professional results across all landscaping applications.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Get Ground Care Quote
              </Link>
              <Link
                to="/attachments"
                className="border border-white text-white hover:bg-white hover:text-green-900 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                View Attachments
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Ground Care Applications</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover how our specialized ground care attachments deliver exceptional results across diverse
                landscaping and maintenance applications.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {applications.map((app, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <img src={app.image || "/placeholder.svg"} alt={app.title} className="w-full h-64 object-cover" />
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{app.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{app.description}</p>
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Recommended Attachments:</h4>
                      <div className="flex flex-wrap gap-2">
                        {app.attachments.map((attachment, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                          >
                            {attachment}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Multihog for Ground Care?</h2>
              <p className="text-lg text-gray-600">
                Experience the advantages of professional-grade ground care equipment
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <benefit.icon className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Attachments Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Ground Care Attachments</h2>
              <p className="text-lg text-gray-600">
                Specialized attachments designed for professional ground care applications
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {attachments.map((attachment, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">{attachment.name}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{attachment.description}</p>
                  <p className="text-green-600 text-xs font-medium">{attachment.specs}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Elevate Your Ground Care?</h2>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              Transform your ground care operations with professional-grade Multihog solutions. Our experts are ready to
              help you choose the perfect configuration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-green-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Schedule Consultation
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-green-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                View Compatible Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default GroundCarePage
